﻿import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, Eye, Package, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { assetsService, type AssetListItem } from '@/services/assets.service';
import { AssetDetailDrawer } from '@/pages/assets/components/AssetDetailDrawer';
import { AssetFilesDialog } from '@/pages/assets/components/AssetFilesDialog';
import {
  AssetBrandModelCell,
  AssetCategoryCell,
  AssetCodeCell,
  AssetFilesCell,
  AssetWarrantyCell,
} from '@/pages/assets/components/AssetTableCells';
import { ASSET_COL } from '@/pages/assets/components/assetTable';
import { DataTable, type Column } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const PAGE_SIZE = 15;

// ── Stat box ──────────────────────────────────────────────────────────────────

interface StatBoxProps {
  label: string;
  value: number | string;
  accent: string;
  icon: React.ReactNode;
}

function StatBox({ label, value, accent, icon }: StatBoxProps) {
  return (
    <div
      className={`rounded-xl border border-slate-200/80 dark:border-border bg-white/80 dark:bg-card/80 px-3 pt-3 pb-2.5 shadow-sm backdrop-blur-sm border-t-4 ${accent}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-xl font-bold text-slate-800 dark:text-slate-100 leading-none">{value}</p>
        <div className="text-slate-400 dark:text-slate-500">{icon}</div>
      </div>
      <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
        {label}
      </p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function MyAssetsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [filesAsset, setFilesAsset] = useState<AssetListItem | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const { data: statsData } = useQuery({
    queryKey: ['my-assets', 'stats', user?.id],
    queryFn: () => assetsService.listMyAssets({ page: 1, limit: 100 }),
    enabled: !!user,
  });

  const params = {
    page,
    limit: PAGE_SIZE,
    q: debouncedSearch || undefined,
    category_id: categoryId || undefined,
    status_id: statusId || undefined,
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['my-assets', 'list', user?.id, params],
    queryFn: () => assetsService.listMyAssets(params),
    enabled: !!user,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['assets', 'categories'],
    queryFn: () => assetsService.listCategories(),
  });

  const { data: statuses = [] } = useQuery({
    queryKey: ['assets', 'statuses'],
    queryFn: () => assetsService.listStatuses(),
  });

  const allItems = statsData?.data ?? [];
  const totalAssigned = statsData?.pagination.total ?? 0;

  const underWarranty = useMemo(
    () =>
      allItems.filter(
        (a) => a.warranty_available && a.warranty_end_date != null && a.warranty_end_date >= today,
      ).length,
    [allItems, today],
  );

  const expiringThirtyDays = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + 30);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return allItems.filter(
      (a) =>
        a.warranty_available &&
        a.warranty_end_date != null &&
        a.warranty_end_date >= today &&
        a.warranty_end_date <= cutoffStr,
    ).length;
  }, [allItems, today]);

  const updateFilter = (setter: (v: string) => void) => (v: string) => {
    setter(v === '__all__' ? '' : v);
    setPage(1);
  };

  const columns: Column<AssetListItem>[] = [
    {
      key: 'photo',
      header: 'Photo',
      className: ASSET_COL.files,
      cell: (row) => (
        <AssetFilesCell row={row} documentBasePath="/assets/my/documents" onOpen={() => setFilesAsset(row)} showInvoice={false} />
      ),
    },
    { key: 'asset_code', header: 'Asset Code', className: ASSET_COL.code, cell: (row) => <AssetCodeCell row={row} /> },
    { key: 'category', header: 'Category', className: ASSET_COL.category, cell: (row) => <AssetCategoryCell row={row} /> },
    { key: 'brand_model', header: 'Brand / Model', className: ASSET_COL.brandModel, cell: (row) => <AssetBrandModelCell row={row} /> },
    {
      key: 'status',
      header: 'Status',
      className: ASSET_COL.status,
      cell: (row) =>
        row.status ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
            style={{ backgroundColor: row.status.color }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
            {row.status.name}
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        ),
    },
    { key: 'warranty', header: 'Warranty', className: ASSET_COL.warranty, cell: (row) => <AssetWarrantyCell row={row} /> },
    {
      key: 'actions',
      header: 'Action',
      className: ASSET_COL.actions,
      cell: (row) => (
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs font-medium"
          onClick={() => setSelectedAssetId(row.id)}
        >
          <Eye className="h-3.5 w-3.5" />
          View
        </Button>
      ),
    },
  ];

  const pagination = data
    ? {
        page: data.pagination.page,
        totalPages: data.pagination.totalPages,
        total: data.pagination.total,
        onPageChange: (p: number) => setPage(p),
      }
    : undefined;

  return (
    <div className="space-y-4">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/account')}
          className="rounded-lg p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-muted transition-colors"
          aria-label="Back to account"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-teal-600" />
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">My Assets</h1>
        </div>
      </div>

      {/* ── Stat strip ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-2">
        <StatBox
          label="Total Assigned"
          value={statsData ? totalAssigned : '—'}
          accent="border-t-sky-500"
          icon={<Package className="h-4 w-4" />}
        />
        <StatBox
          label="Under Warranty"
          value={statsData ? underWarranty : '—'}
          accent="border-t-amber-500"
          icon={<ShieldCheck className="h-4 w-4" />}
        />
        <StatBox
          label="Expiring Soon"
          value={statsData ? expiringThirtyDays : '—'}
          accent="border-t-rose-500"
          icon={<AlertCircle className="h-4 w-4" />}
        />
      </div>

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search code, category, brand, model…"
          containerClassName="min-w-[220px] flex-1 sm:max-w-sm"
        />

        <Select value={categoryId || '__all__'} onValueChange={updateFilter(setCategoryId)}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusId || '__all__'} onValueChange={updateFilter(setStatusId)}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Statuses</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                  {s.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {data && (
          <span className="ml-auto text-xs text-muted-foreground">
            {data.pagination.total} {data.pagination.total === 1 ? 'asset' : 'assets'}
          </span>
        )}
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        loading={isLoading}
        error={isError}
        onRetry={() => void queryClient.invalidateQueries({ queryKey: ['my-assets', 'list'] })}
        empty="No assets are assigned to you"
        pagination={pagination}
      />

      <AssetDetailDrawer
        assetId={selectedAssetId}
        onClose={() => setSelectedAssetId(null)}
        fetchFn={assetsService.getMyAsset}
        documentBasePath="/assets/my/documents"
        hideInvoices
      />

      <AssetFilesDialog asset={filesAsset} onClose={() => setFilesAsset(null)} selfService />
    </div>
  );
}
